import {
  type DocumentDetail,
  IVA_RATES,
  labelOf,
  PAYMENT_METHODS,
  REFERENCE_CODES,
  SALE_CONDITIONS,
} from '@facture/contracts'
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, type ErrorComponentProps, Link, useNavigate } from '@tanstack/react-router'
import {
  AlertTriangleIcon,
  ArrowLeftIcon,
  CheckCircle2Icon,
  CheckIcon,
  CircleDashedIcon,
  CopyIcon,
  DownloadIcon,
  FileCodeIcon,
  FileMinus2Icon,
  FilePlus2Icon,
  FileSearchIcon,
  FileTextIcon,
  LinkIcon,
  Loader2Icon,
  MailIcon,
  MailWarningIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PrinterIcon,
  ReceiptTextIcon,
  RefreshCwIcon,
  RepeatIcon,
  SendIcon,
  ShieldCheckIcon,
  Trash2Icon,
  WalletIcon,
  XCircleIcon,
} from 'lucide-react'
import { m } from 'motion/react'
import { type ReactNode, useState } from 'react'
import { toast } from 'sonner'
import { DURATION, TRANSITION } from '@/components/motion'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { confirm } from '@/components/ui/confirm'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip } from '@/components/ui/misc'
import { Page } from '@/components/ui/page'
import { Skeleton } from '@/components/ui/skeleton'
import { documentQuery, downloadFile, printPdf, useDocumentAction } from '@/features/documents/api'
import { PaymentDialog, PaymentsCard } from '@/features/documents/payments-card'
import { SendEmailDialog } from '@/features/documents/send-email-dialog'
import { HaciendaBadge, HaciendaStatusIcon, KIND_LABEL, PaymentBadge } from '@/features/documents/status'
import { ApiRequestError, api, errorMessage } from '@/lib/api'
import { copyText } from '@/lib/clipboard'
import { formatDate, formatDateTime, formatIdentification, formatMoney } from '@/lib/format'
import { companyQuery } from '@/lib/queries'
import { useCan } from '@/lib/session'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_app/documents/$id')({
  staticData: { crumb: 'Comprobante' },
  loader: ({ context, params }) => context.queryClient.ensureQueryData(documentQuery(params.id)),
  pendingComponent: () => (
    <div className="mx-auto grid gap-6 px-8 py-8 xl:grid-cols-[1fr_360px]">
      <Skeleton className="h-[640px] rounded-xl" />
      <Skeleton className="h-[640px] rounded-xl" />
    </div>
  ),
  component: DocumentPage,
  errorComponent: DocumentError,
})

/** Un comprobante inexistente o de otra compañía (404 del API) se muestra como tal, no como un error genérico. */
function DocumentError({ error, reset }: ErrorComponentProps) {
  const missing = error instanceof ApiRequestError && [400, 403, 404].includes(error.statusCode)
  return (
    <div className="flex min-h-[60dvh] flex-col items-center justify-center gap-4 px-6 text-center">
      <div
        className={cn(
          'flex size-12 items-center justify-center rounded-2xl',
          missing ? 'bg-primary-soft text-primary' : 'bg-destructive-soft text-destructive',
        )}
      >
        {missing ? <FileSearchIcon className="size-6" /> : <AlertTriangleIcon className="size-6" />}
      </div>
      <div>
        <h1 className="font-semibold text-xl">{missing ? 'Comprobante no encontrado' : 'Algo salió mal'}</h1>
        <p className="mt-1 text-muted-foreground text-sm">
          {missing
            ? 'No existe o pertenece a otra compañía. Revisá que la dirección sea correcta y que tengas la compañía correcta seleccionada.'
            : 'No pudimos mostrar este comprobante. Reintentá o volvé al listado.'}
        </p>
      </div>
      <div className="flex gap-2">
        {!missing && (
          <Button variant="outline" onClick={reset}>
            Reintentar
          </Button>
        )}
        <Button asChild>
          <Link to="/invoices">Ir a facturas</Link>
        </Button>
      </div>
    </div>
  )
}

const number = (doc: DocumentDetail) =>
  doc.consecutive ? doc.consecutive.slice(10).replace(/^0+/, '') : 'Borrador'

function DocumentPage() {
  const { id } = Route.useParams()
  const { data: doc } = useSuspenseQuery(documentQuery(id))
  const navigate = useNavigate()
  const canIssue = useCan('documents:issue')
  const action = useDocumentAction()
  const [emailOpen, setEmailOpen] = useState(false)
  const [emailKind, setEmailKind] = useState<'document' | 'reminder'>('document')
  const [payOpen, setPayOpen] = useState(false)
  const canPay = useCan('payments:write')
  const queryClient = useQueryClient()
  const money = (v: number) => formatMoney(v, doc.currency as 'CRC')
  const backTo =
    doc.kind === 'TE'
      ? '/tickets'
      : doc.kind === 'FEC'
        ? '/purchases'
        : doc.kind === 'FE'
          ? '/invoices'
          : '/notes'
  const isDraft = doc.status === 'draft'
  const reminders = doc.events.filter((e) => e.type === 'payment_reminder').length
  const canNote =
    !isDraft && ['FE', 'TE'].includes(doc.kind) && ['accepted', 'internal'].includes(doc.haciendaStatus)

  async function copyPublicLink() {
    try {
      const { url } = await api.get<{ url: string }>(`/documents/${doc.id}/public-link`)
      await copyText(url, {
        label: 'Enlace público del comprobante',
        successMessage: 'Enlace copiado: cualquiera que lo tenga puede ver este comprobante',
      })
    } catch (error) {
      toast.error(errorMessage(error))
    }
  }

  async function download(kind: 'pdf' | 'signed' | 'response' | 'json') {
    try {
      if (kind === 'pdf') await downloadFile(`/documents/${doc.id}/pdf`, `${doc.key ?? doc.id}.pdf`)
      else if (kind === 'json') {
        const data = await api.get<unknown>(`/documents/${doc.id}/json`)
        const url = URL.createObjectURL(
          new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }),
        )
        Object.assign(document.createElement('a'), {
          href: url,
          download: `${doc.key ?? doc.id}.json`,
        }).click()
        setTimeout(() => URL.revokeObjectURL(url), 1000)
      } else
        await downloadFile(
          `/documents/${doc.id}/xml/${kind}`,
          `${doc.key}${kind === 'response' ? '-respuesta' : ''}.xml`,
        )
    } catch (error) {
      toast.error(errorMessage(error))
    }
  }

  return (
    <Page>
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-3">
          <Button asChild variant="ghost" size="icon-sm" className="mt-1">
            <Link to={backTo} aria-label="Volver">
              <ArrowLeftIcon />
            </Link>
          </Button>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-semibold text-2xl tracking-tight">
                {KIND_LABEL[doc.kind].singular} <span className="text-muted-foreground">#{number(doc)}</span>
              </h1>
              {isDraft ? <Badge>Borrador</Badge> : <HaciendaBadge status={doc.haciendaStatus} />}
              {doc.kind === 'FE' && !isDraft && doc.paymentStatus !== 'na' && (
                <PaymentBadge status={doc.paymentStatus} />
              )}
            </div>
            <p className="mt-1 text-muted-foreground text-sm">
              {doc.receiver?.name ?? 'Consumidor final'} ·{' '}
              {doc.issuedAt ? formatDateTime(doc.issuedAt) : `creado ${formatDate(doc.createdAt)}`}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {isDraft && canIssue && (
            <>
              <Button
                variant="ghost"
                onClick={() =>
                  confirm({
                    title: '¿Eliminar este borrador?',
                    destructive: true,
                    confirmLabel: 'Eliminar',
                    onConfirm: async () => {
                      await api.delete(`/documents/${doc.id}`)
                      toast.success('Borrador eliminado')
                      navigate({ to: backTo })
                    },
                  })
                }
              >
                <Trash2Icon /> Eliminar
              </Button>
              <Button asChild variant="outline">
                <Link to="/documents/edit/$id" params={{ id: doc.id }}>
                  <PencilIcon /> Editar
                </Link>
              </Button>
              <Button
                loading={action.isPending}
                onClick={() =>
                  action.mutate(
                    { id: doc.id, action: 'issue' },
                    { onSuccess: () => toast.success('Comprobante emitido') },
                  )
                }
              >
                <SendIcon /> Emitir
              </Button>
            </>
          )}
          {!isDraft && (
            <>
              {canPay &&
                doc.kind === 'FE' &&
                doc.balance > 0.005 &&
                ['accepted', 'internal'].includes(doc.haciendaStatus) && (
                  <Tooltip content="Pagar" side="bottom">
                    <Button size="icon" aria-label="Pagar" onClick={() => setPayOpen(true)}>
                      <WalletIcon />
                    </Button>
                  </Tooltip>
                )}
              <DropdownMenu>
                <Tooltip content="Imprimir" side="bottom">
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" aria-label="Imprimir">
                      <PrinterIcon />
                    </Button>
                  </DropdownMenuTrigger>
                </Tooltip>
                <DropdownMenuContent align="end" className="w-60">
                  <DropdownMenuItem
                    onSelect={() => printPdf(doc.id).catch((e) => toast.error(errorMessage(e)))}
                  >
                    <PrinterIcon /> Hoja carta
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => printPdf(doc.id, 'pos').catch((e) => toast.error(errorMessage(e)))}
                  >
                    <ReceiptTextIcon /> Tiquete POS (80 mm)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Tooltip content="PDF" side="bottom">
                <Button variant="outline" size="icon" aria-label="PDF" onClick={() => download('pdf')}>
                  <DownloadIcon />
                </Button>
              </Tooltip>
              <DropdownMenu>
                <Tooltip content="XML" side="bottom">
                  <span className="inline-flex">
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon" aria-label="XML" disabled={!doc.hasSignedXml}>
                        <FileCodeIcon />
                      </Button>
                    </DropdownMenuTrigger>
                  </span>
                </Tooltip>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => download('signed')}>
                    <FileCodeIcon /> Comprobante firmado
                  </DropdownMenuItem>
                  <DropdownMenuItem disabled={!doc.hasResponseXml} onSelect={() => download('response')}>
                    <ShieldCheckIcon /> Respuesta de Hacienda
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => download('json')}>
                    <FileCodeIcon /> Datos en JSON
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {doc.kind !== 'FEC' && !isDraft && doc.haciendaStatus !== 'rejected' && (
                <Tooltip content="Enviar" side="bottom">
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Enviar"
                    onClick={() => {
                      setEmailKind('document')
                      setEmailOpen(true)
                    }}
                  >
                    <MailIcon />
                  </Button>
                </Tooltip>
              )}
              {canIssue && (
                <DropdownMenu>
                  <Tooltip content="Acciones" side="bottom">
                    <DropdownMenuTrigger asChild>
                      <Button size="icon" aria-label="Acciones">
                        <MoreHorizontalIcon />
                      </Button>
                    </DropdownMenuTrigger>
                  </Tooltip>
                  <DropdownMenuContent align="end" className="w-60">
                    <DropdownMenuLabel>Corregir</DropdownMenuLabel>
                    <DropdownMenuItem disabled={!canNote} asChild>
                      <Link to="/documents/new" search={{ kind: 'NC', from: doc.id }}>
                        <FileMinus2Icon /> Nota de crédito
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem disabled={!canNote} asChild>
                      <Link to="/documents/new" search={{ kind: 'ND', from: doc.id }}>
                        <FilePlus2Icon /> Nota de débito
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link
                        to="/documents/new"
                        search={{
                          kind: ['NC', 'ND'].includes(doc.kind) ? 'FE' : (doc.kind as 'FE'),
                          from: doc.id,
                        }}
                      >
                        <CopyIcon /> Duplicar como nuevo
                      </Link>
                    </DropdownMenuItem>
                    {['FE', 'TE'].includes(doc.kind) && (
                      <DropdownMenuItem asChild>
                        <Link to="/quotes/new" search={{ kind: 'recurring', from: doc.id }}>
                          <RepeatIcon /> Convertir en recurrente
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {canPay && doc.kind === 'FE' && doc.balance > 0.005 && (
                      <DropdownMenuItem
                        onSelect={() => {
                          setEmailKind('reminder')
                          setEmailOpen(true)
                        }}
                      >
                        <MailWarningIcon /> Recordatorio de pago
                        {reminders > 0 ? ` (${reminders})` : ''}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onSelect={copyPublicLink}>
                      <LinkIcon /> Copiar enlace público
                    </DropdownMenuItem>
                    {['error', 'pending', 'processing', 'sent'].includes(doc.haciendaStatus) && (
                      <DropdownMenuItem
                        onSelect={() =>
                          action.mutate(
                            { id: doc.id, action: 'retry' },
                            { onSuccess: () => toast.success('Reenvío en curso') },
                          )
                        }
                      >
                        <RefreshCwIcon /> Reintentar con Hacienda
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </>
          )}
        </div>
      </div>

      <StatusBanner
        doc={doc}
        onRetry={() => action.mutate({ id: doc.id, action: 'retry' })}
        retrying={action.isPending}
      />

      <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        <PaperPreview doc={doc} money={money} />
        <div className="grid gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Datos fiscales</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 text-sm">
              {doc.key && <CopyRow label="Clave" value={doc.key} />}
              {doc.consecutive && <CopyRow label="Consecutivo" value={doc.consecutive} />}
              <InfoRow label="Condición">
                {labelOf(SALE_CONDITIONS, doc.saleCondition)}
                {doc.creditDays ? ` · ${doc.creditDays} días` : ''}
              </InfoRow>
              <InfoRow label="Medio de pago">
                {doc.paymentMethods.map((m) => labelOf(PAYMENT_METHODS, m)).join(', ')}
              </InfoRow>
              {doc.dueDate && <InfoRow label="Vence">{formatDate(doc.dueDate)}</InfoRow>}
              {doc.kind === 'FE' && !isDraft && (
                <>
                  <InfoRow label="Monto pagado">{money(doc.paidAmount)}</InfoRow>
                  <InfoRow label="Saldo">{money(doc.balance)}</InfoRow>
                </>
              )}
              {doc.currency !== 'CRC' && (
                <InfoRow label="Tipo de cambio">{formatMoney(doc.exchangeRate)}</InfoRow>
              )}
              <InfoRow label="Sucursal · terminal">
                {doc.branchCode} · {doc.terminalCode}
              </InfoRow>
              {doc.reference && (
                <InfoRow label="Referencia">
                  {doc.reference.documentId ? (
                    <Link
                      to="/documents/$id"
                      params={{ id: doc.reference.documentId }}
                      className="text-primary hover:underline"
                    >
                      #{doc.reference.consecutive?.slice(10).replace(/^0+/, '')}
                    </Link>
                  ) : (
                    doc.reference.key?.slice(21, 41)
                  )}{' '}
                  · {labelOf(REFERENCE_CODES, doc.reference.code)}
                </InfoRow>
              )}
            </CardContent>
          </Card>

          {doc.kind === 'FE' && !isDraft && <PaymentsCard doc={doc} />}

          {doc.relatedNotes.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Notas relacionadas</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-2">
                {doc.relatedNotes.map((n) => (
                  <Link
                    key={n.id}
                    to="/documents/$id"
                    params={{ id: n.id }}
                    className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm transition hover:bg-accent"
                  >
                    <span className="flex items-center gap-2">
                      <Badge variant={n.kind === 'NC' ? 'primary' : 'info'}>{n.kind}</Badge>#
                      {n.consecutive?.slice(10).replace(/^0+/, '')}
                    </span>
                    <span className="flex items-center gap-2">
                      {money(n.total)} <HaciendaStatusIcon status={n.haciendaStatus} size="sm" />
                    </span>
                  </Link>
                ))}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Actividad</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="relative grid gap-4 border-border border-l pl-5">
                {doc.events.map((event) => (
                  <li key={event.id} className="relative">
                    <span
                      className={cn(
                        'absolute top-1 -left-[25px] size-2.5 rounded-full ring-4 ring-card',
                        event.type === 'accepted' && 'bg-success',
                        event.type === 'rejected' && 'bg-destructive',
                        event.type === 'error' && 'bg-warning',
                        !['accepted', 'rejected', 'error'].includes(event.type) && 'bg-primary',
                      )}
                    />
                    <div className="font-medium text-sm">{EVENT_LABEL[event.type] ?? event.type}</div>
                    {event.message && (
                      <div className="mt-0.5 text-muted-foreground text-xs leading-relaxed">
                        {event.message}
                      </div>
                    )}
                    <div className="mt-0.5 text-muted-foreground/80 text-xs">
                      {formatDateTime(event.createdAt)}
                      {event.userName && ` · ${event.userName}`}
                    </div>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </div>
      </div>
      <SendEmailDialog doc={doc} open={emailOpen} onOpenChange={setEmailOpen} initialKind={emailKind} />
      <PaymentDialog
        doc={doc}
        open={payOpen}
        onClose={() => setPayOpen(false)}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ['documents'] })
          queryClient.invalidateQueries({ queryKey: ['payments'] })
          queryClient.invalidateQueries({ queryKey: ['customers'] })
        }}
      />
    </Page>
  )
}

const EVENT_LABEL: Record<string, string> = {
  created: 'Creado',
  updated: 'Borrador actualizado',
  issued: 'Emitido',
  signed: 'Firmado digitalmente',
  sent: 'Enviado a Hacienda',
  accepted: 'Aceptado por Hacienda',
  rejected: 'Rechazado por Hacienda',
  error: 'Incidente',
  retry: 'Reintento solicitado',
  emailed: 'Enviado por correo',
  payment: 'Cobro registrado',
  payment_voided: 'Cobro anulado',
}

function StatusBanner({
  doc,
  onRetry,
  retrying,
}: {
  doc: DocumentDetail
  onRetry: () => void
  retrying: boolean
}) {
  if (doc.status === 'draft') {
    // Un borrador admite líneas sin CABYS; para emitir hace falta completarlas.
    const incomplete = doc.lines.filter((l) => !l.cabysCode)
    if (!incomplete.length) return null
    return (
      <Banner tone="warning" icon={AlertTriangleIcon} title="Borrador incompleto">
        {incomplete.length === 1 ? 'La línea' : 'Las líneas'} {incomplete.map((l) => l.lineNumber).join(', ')}{' '}
        {incomplete.length === 1 ? 'no tiene' : 'no tienen'} CABYS. Editá el borrador y completalo antes de
        emitir.
      </Banner>
    )
  }
  const inFlight = ['pending', 'sent', 'processing'].includes(doc.haciendaStatus)
  if (doc.haciendaStatus === 'accepted') {
    return (
      <Banner tone="success" icon={CheckCircle2Icon} title="Aceptado por Hacienda">
        {doc.acceptedAt && `Confirmado el ${formatDateTime(doc.acceptedAt)}.`}{' '}
        {doc.emailTo.length > 0 && `Se envió a ${doc.emailTo.join(', ')}.`}
      </Banner>
    )
  }
  if (doc.haciendaStatus === 'rejected') {
    return (
      <Banner tone="destructive" icon={XCircleIcon} title="Rechazado por Hacienda">
        {doc.haciendaMessage ?? 'Hacienda no indicó el motivo.'} Emití un comprobante nuevo corrigiendo el
        problema.
      </Banner>
    )
  }
  if (doc.haciendaStatus === 'error') {
    return (
      <Banner
        tone="warning"
        icon={AlertTriangleIcon}
        title="El comprobante no se pudo enviar"
        action={
          <Button size="sm" variant="outline" loading={retrying} onClick={onRetry}>
            <RefreshCwIcon /> Reintentar
          </Button>
        }
      >
        {doc.lastError}
      </Banner>
    )
  }
  if (inFlight) {
    const steps = [
      { key: 'pending', label: 'Firmando' },
      { key: 'sent', label: 'Enviado' },
      { key: 'processing', label: 'Hacienda procesa' },
    ]
    const current = steps.findIndex((s) => s.key === doc.haciendaStatus)
    return (
      <Card className="mb-6 overflow-hidden">
        <div className="flex flex-wrap items-center gap-6 px-5 py-4">
          <Loader2Icon className="size-5 animate-spin text-info" />
          <div className="flex flex-1 flex-wrap items-center gap-2 text-sm">
            {steps.map((step, i) => (
              <span key={step.key} className="flex items-center gap-2">
                {i < current ? (
                  <CheckIcon className="size-4 text-success" />
                ) : i === current ? (
                  <CircleDashedIcon className="size-4 animate-spin text-info [animation-duration:3s]" />
                ) : (
                  <CircleDashedIcon className="size-4 text-muted-foreground/50" />
                )}
                <span className={i <= current ? 'font-medium' : 'text-muted-foreground'}>{step.label}</span>
                {i < steps.length - 1 && <span className="mx-1 h-px w-6 bg-border" />}
              </span>
            ))}
          </div>
          {doc.lastError && <span className="text-muted-foreground text-xs">{doc.lastError}</span>}
        </div>
        {/* Progreso con transform (scaleX): avanza suave al cambiar de estado sin recalcular el layout. */}
        <m.div
          className="h-0.5 w-full origin-left bg-brand-gradient"
          initial={{ scaleX: 0.05 }}
          animate={{ scaleX: (current + 1) / (steps.length + 1) }}
          transition={{ ...TRANSITION.enter, duration: DURATION.slower }}
        />
      </Card>
    )
  }
  return null
}

function Banner({
  tone,
  icon: Icon,
  title,
  children,
  action,
}: {
  tone: 'success' | 'destructive' | 'warning'
  icon: typeof CheckCircle2Icon
  title: string
  children?: ReactNode
  action?: ReactNode
}) {
  const tones = {
    success: 'border-success/25 bg-success-soft text-success-soft-foreground',
    destructive: 'border-destructive/25 bg-destructive-soft text-destructive-soft-foreground',
    warning: 'border-warning/30 bg-warning-soft text-warning-soft-foreground',
  }
  return (
    <div
      className={cn(
        'mb-6 flex flex-col gap-3 rounded-xl border px-5 py-4 sm:flex-row sm:items-center',
        tones[tone],
      )}
    >
      <Icon className="size-5 shrink-0" />
      <div className="flex-1 text-sm">
        <div className="font-semibold">{title}</div>
        {children && <div className="mt-0.5 opacity-90">{children}</div>}
      </div>
      {action}
    </div>
  )
}

function PaperPreview({ doc, money }: { doc: DocumentDetail; money: (v: number) => string }) {
  const company = useQuery(companyQuery())
  const s = doc.summary
  const emitter = company.data
  return (
    <Card className="overflow-hidden">
      <div className="h-1.5 bg-brand-gradient" />
      <div className="p-6 sm:p-10">
        <div className="flex flex-col gap-6 sm:flex-row sm:justify-between">
          <div>
            <div className="font-semibold text-lg">{emitter?.tradeName ?? emitter?.legalName}</div>
            {emitter?.tradeName && <div className="text-muted-foreground text-sm">{emitter.legalName}</div>}
            <div className="mt-1 text-muted-foreground text-sm">
              {emitter && formatIdentification(emitter.identificationType, emitter.identification)} ·{' '}
              {emitter?.email}
            </div>
          </div>
          <div className="sm:text-right">
            <div className="font-medium text-muted-foreground text-xs tracking-wide">
              {KIND_LABEL[doc.kind].singular.toUpperCase()}
            </div>
            <div className="mt-1 font-semibold font-mono text-lg">#{number(doc)}</div>
            <div className="text-muted-foreground text-sm">
              {doc.issuedAt ? formatDate(doc.issuedAt, "d 'de' MMMM yyyy") : 'Sin emitir'}
            </div>
          </div>
        </div>

        <div className="mt-8 grid gap-4 rounded-xl bg-subtle p-4 sm:grid-cols-2">
          <div>
            <div className="font-medium text-muted-foreground text-xs">
              {doc.kind === 'FEC' ? 'Proveedor' : 'Cliente'}
            </div>
            <div className="mt-1 font-medium">{doc.receiver?.name ?? 'Consumidor final'}</div>
            {doc.receiver?.identification && (
              <div className="font-mono text-muted-foreground text-sm">
                {formatIdentification(doc.receiver.identificationType ?? '', doc.receiver.identification)}
              </div>
            )}
            {doc.receiver?.email && <div className="text-muted-foreground text-sm">{doc.receiver.email}</div>}
          </div>
          <div className="sm:text-right">
            <div className="font-medium text-muted-foreground text-xs">Total</div>
            <div className="mt-1 font-semibold text-2xl tracking-tight tabular">{money(doc.total)}</div>
            {doc.kind === 'FE' && doc.balance > 0.005 && (
              <div className="text-muted-foreground text-sm">Saldo {money(doc.balance)}</div>
            )}
          </div>
        </div>

        <div className="mt-8 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-border border-b text-muted-foreground text-xs">
                <th className="py-2 pr-3 text-left font-medium">Descripción</th>
                <th className="px-3 py-2 text-right font-medium">Cant.</th>
                <th className="px-3 py-2 text-right font-medium">Precio</th>
                <th className="px-3 py-2 text-right font-medium">IVA</th>
                <th className="py-2 pl-3 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {doc.lines.map((l) => (
                <tr key={l.id} className="border-border border-b last:border-0">
                  <td className="py-3 pr-3">
                    <div className="font-medium">{l.description}</div>
                    {l.notes && <div className="text-muted-foreground text-xs italic">{l.notes}</div>}
                    <div className="text-muted-foreground text-xs">
                      {(l.codes.length ? l.codes.map((c) => c.code) : l.code ? [l.code] : []).length > 0 && (
                        <span className="font-mono">
                          Cód. {(l.codes.length ? l.codes.map((c) => c.code) : [l.code]).join(', ')}
                          {l.cabysCode ? ' · ' : ''}
                        </span>
                      )}
                      {l.cabysCode && <span className="font-mono">CABYS {l.cabysCode}</span>}
                      {l.discountAmount > 0 && ` · desc. ${l.discountPct}%`}
                      {l.exoneration && ` · exonerado ${l.exoneration.rate}%`}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right tabular">
                    {l.quantity} <span className="text-muted-foreground text-xs">{l.unit}</span>
                  </td>
                  <td className="px-3 py-3 text-right tabular">{money(l.unitPrice)}</td>
                  <td className="px-3 py-3 text-right tabular">
                    {IVA_RATES.find((r) => r.code === l.ivaCode)?.rate ?? l.taxRate}%
                  </td>
                  <td className="py-3 pl-3 text-right font-medium tabular">{money(l.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 ml-auto grid max-w-xs gap-1.5 text-sm">
          <Line label="Subtotal" value={money(s.totalSale)} />
          {s.totalDiscount > 0 && <Line label="Descuentos" value={`−${money(s.totalDiscount)}`} />}
          {s.taxBreakdown.map((t) => (
            <Line
              key={t.ivaCode}
              label={`IVA ${IVA_RATES.find((r) => r.code === t.ivaCode)?.rate ?? ''}%`}
              value={money(t.amount)}
            />
          ))}
          {s.totalExonerated > 0 && <Line label="Exonerado" value={money(s.totalExonerated)} />}
          {s.totalOtherCharges > 0 && <Line label="Otros cargos" value={money(s.totalOtherCharges)} />}
          {s.totalIvaReturned > 0 && <Line label="IVA devuelto" value={`−${money(s.totalIvaReturned)}`} />}
          <div className="mt-2 flex justify-between border-border border-t pt-2 font-semibold text-base">
            <span>Total</span>
            <span className="tabular">{money(s.total)}</span>
          </div>
        </div>

        {(doc.notes || doc.terms) && (
          <div className="mt-8 grid gap-4 border-border border-t pt-6 text-sm sm:grid-cols-2">
            {doc.notes && (
              <div>
                <div className="font-medium text-muted-foreground text-xs">Observaciones</div>
                <p className="mt-1 whitespace-pre-line">{doc.notes}</p>
              </div>
            )}
            {doc.terms && (
              <div>
                <div className="font-medium text-muted-foreground text-xs">Términos</div>
                <p className="mt-1 whitespace-pre-line text-muted-foreground">{doc.terms}</p>
              </div>
            )}
          </div>
        )}
        <div className="mt-8 flex items-center gap-2 text-muted-foreground text-xs">
          <FileTextIcon className="size-3.5" /> Comprobante electrónico según la resolución
          MH-DGT-RES-0027-2024, versión 4.4
        </div>
      </div>
    </Card>
  )
}

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular">{value}</span>
    </div>
  )
}

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="text-right">{children}</span>
    </div>
  )
}

function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div>
      <div className="text-muted-foreground">{label}</div>
      <Tooltip content={copied ? 'Copiado' : 'Copiar'}>
        <button
          type="button"
          onClick={async () => {
            if (!(await copyText(value, { label, successMessage: false }))) return
            setCopied(true)
            setTimeout(() => setCopied(false), 1200)
          }}
          className="mt-1 flex w-full items-center gap-2 rounded-md bg-muted px-2 py-1.5 text-left font-mono text-[11.5px] break-all transition hover:bg-accent"
        >
          <span className="flex-1">{value}</span>
          {copied ? (
            <CheckIcon className="size-3.5 shrink-0 text-success" />
          ) : (
            <CopyIcon className="size-3.5 shrink-0 text-muted-foreground" />
          )}
        </button>
      </Tooltip>
    </div>
  )
}
